
import gql from 'graphql-tag';
import productPromotionFragment from '~/apollo/fragments/productPromotionFragment';
import termLifePlanScenariosQuery from '~/apollo/queries/product/termLifePlanScenario.gql';
import termLifeBenchmark from '~/apollo/queries/product/termLifeBenchmark';
import termLifeTooltips from '~/apollo/queries/product/tooltips/termlifeTooltips';
import commonProductDetailMixins from '~/mixins/commonProductDetailMixins.js';
import productPromotionMixins from '~/mixins/productPromotionMixins.js';
import productShortcutMixins from "~/mixins/productShortcutMixins.js";
import productInsuranceNameMixins from '~/mixins/productInsuranceNameMixins';

export default {
  mixins: [commonProductDetailMixins, productPromotionMixins, productShortcutMixins, productInsuranceNameMixins],

  data() {
    return {
      termLifePlanScenarios: null,
      loading: 0,
      age: this.$route.query.age || '35',
      gender: this.$route.query.gender || '1',
      targetPeriod: this.$route.query.targetPeriod || '10',
      sum_assured: this.$route.query.sum_assured || '1000000',
      currentLocale: this.$normalizeLocale(this.$i18n.locale),
    };
  },
  // page component definitions
  apollo: {
    $loadingKey: 'loading',
    termLifePlanScenarios: {
      query: gql`
        ${termLifePlanScenariosQuery}
        ${productPromotionFragment}
      `,
      prefetch: true,
      variables() {
        return {
          scenario_code: this.$route.params.slug,
          locale: this.currentLocale,
        };
      },
    },
    termLifeBenchmarks: {
      query: termLifeBenchmark,
      prefetch: true,
      variables() {
        return {
          gender: parseInt(this.gender),
          age: parseInt(this.age),
          sum_assured: parseInt(this.sum_assured),
        };
      },
    },
    termLifeTooltips: {
      query: termLifeTooltips,
      prefetch: true,
      variables() {
        return { currentlocale: this.$normalizeLocale(this.$i18n.locale) };
      },
      update(data) {
        return this.$normalizeStrapi(data.infoTooltip.data);
      },
    },
  },
  computed: {
    simpleItems() {
      if (this.termLifePlanScenarios?.data[0]) {
        const item = this.termLifePlanScenarios.data[0];
        let promotionPrices = Array;
        promotionPrices = [];
        let originalPrices = Array;
        originalPrices = [];
        const premium = item.attributes.term_life_plan_premium;
        const age = parseInt(this.age);
        const renewalTerm = item.attributes.renewal_term;
        const sumAssured = item.attributes.sum_assured;
        originalPrices = this.getPremium(premium, age, renewalTerm, sumAssured);
        const fyp = parseFloat(originalPrices[0]).toFixed(0);

        let tenYearConstant = 0;
        let twentyYearConstant = 0;
        // get termlife rating calculation constant
        this.termLifeBenchmarks.data.forEach((item) => {
          if(item.attributes.Year === 10)
            tenYearConstant = item.attributes.BenchmarkValue;
          if(item.attributes.Year === 20)
            twentyYearConstant = item.attributes.BenchmarkValue;
        });

        // console.log("10YearConstant: "+ tenYearConstant);
        // console.log("20YearConstant: "+ twentyYearConstant);

        let discountedTenYearAvgPremium = null;
        let discountedTwentyYearAvgPremium = null;
        let tenYearRating = item.attributes.ten_year_rating?item.attributes.ten_year_rating:0;
        let twentyYearRating = item.attributes.twenty_year_rating?item.attributes.twenty_year_rating:0;
        let tenYearDeathScore = item.attributes.ten_year_death_score;
        let twentyYearDeathScore = item.attributes.twenty_year_death_score;
        let promotions = null;
        let promotion = null;
        let promotion_last_call = null;

        const shortcut = this.getProductShortcut(item.attributes);

        const planName = item.attributes.term_life_plan.data.attributes.term_life_plan_translations.data[0].attributes.name;
        const insurer = item.attributes.term_life_plan.data.attributes.product.data.attributes.insurance_company.data.attributes.insurance_company_translations.data[0].attributes.name;
        let decoderCTALink = this.$generateWhatsappCTALink(this.$t('products.termLife.tl_categoryName'), insurer, planName);
        let decoderCTAText = '';

        // check scenario level promo first , then plan level promotion
        if (item.attributes.product_promotions.data?.length > 0) {
          promotions = item.attributes.product_promotions.data;
        }
        if (item.attributes.term_life_plan.data.attributes.product_promotions.data?.length > 0) {
          promotions = item.attributes.term_life_plan.data.attributes.product_promotions.data;
        }

        if(promotions != null) {
          promotion = this.checkPromotionValid(promotions);
          promotion_last_call = this.checkPromotionLastCall(promotion);

          if (!promotion) {
            promotion = null;
          } else {
            if (promotion.attributes?.product_promotion_translations?.data[0]?.attributes?.DecoderCTALink) {
              decoderCTALink =
                promotion.attributes?.product_promotion_translations?.data[0]?.attributes?.DecoderCTALink;
              decoderCTAText = this.$t('products.product_website');
            }
            const discounts = promotion.attributes.DiscountPercentage;
            if (discounts) {
              // calculate promotion price and avg premium

              const calulatedValue = this.calculateDiscountedPremium(originalPrices, discounts);
              promotionPrices = calulatedValue.promotionPrices;
              discountedTenYearAvgPremium = calulatedValue.discountedTenYearAvgPremium;
              discountedTwentyYearAvgPremium = calulatedValue.discountedTwentyYearAvgPremium;

              if (tenYearConstant !== 0 && twentyYearConstant !== 0) {
                // calculate 10year rating and 20year rating
                tenYearRating =
                  0.8 *
                  Math.min((sumAssured / discountedTenYearAvgPremium / tenYearConstant) * 10, 10) +
                  0.2 * item.attributes.coverage_score;
                twentyYearRating =
                  0.8 *
                  Math.min(
                    (sumAssured / discountedTwentyYearAvgPremium / twentyYearConstant) * 10,
                    10
                  ) +
                  0.2 * item.attributes.coverage_score;
                tenYearDeathScore = Math.min(
                  (sumAssured / discountedTenYearAvgPremium / tenYearConstant) * 10,
                  10
                );
                twentyYearDeathScore = Math.min(
                  (sumAssured / discountedTwentyYearAvgPremium / twentyYearConstant) * 10,
                  10
                );
              }

              if (discountedTenYearAvgPremium === parseInt(item.attributes.ten_year_avg_premium.toFixed(0))) {
                discountedTenYearAvgPremium = null;
              }

              if (discountedTwentyYearAvgPremium === parseInt(item.attributes.twenty_year_avg_premium.toFixed(0))) {
                discountedTwentyYearAvgPremium = null;
              }
            }
          }
        }

        const filteredTranslations = this.getFilteredTranslations(item.attributes.term_life_plan.data.attributes.product.data.attributes.insurance_company.data.attributes
        .insurance_company_translations.data[0].attributes.localizations.data);

        return {
          // ***** OVERVIEW *****
          scenario_code: item.attributes.scenario_code,
          // ten_year_rating: item.attributes.ten_year_rating,
          // twenty_year_rating: item.attributes.twenty_year_rating,
          ten_year_rating: tenYearRating,
          twenty_year_rating: twentyYearRating,
          ten_year_death_score: tenYearDeathScore,
          twenty_year_death_score: twentyYearDeathScore,
          ten_year_avg_premium: item.attributes.ten_year_avg_premium,
          twenty_year_avg_premium: item.attributes.twenty_year_avg_premium,
          coverage_score: item.attributes.coverage_score?item.attributes.coverage_score:0,
          renewal_term: item.attributes.renewal_term,
          overall_star: item.attributes.overall_star,
          sum_assured: item.attributes.sum_assured,
          shortcut: shortcut,

          // insurer
          insurer_name_first:
            item.attributes.term_life_plan.data.attributes.product.data.attributes.insurance_company.data.attributes
              .insurance_company_translations.data[0].attributes.name,
          insurer_name_second:
            filteredTranslations[0].attributes.name,
          insurer_logo:
            item.attributes.term_life_plan.data.attributes.product.data.attributes.insurance_company.data.attributes
              .logo,
          insurer_code:
            item.attributes.term_life_plan.data.attributes.product.data.attributes.insurance_company.data.attributes
              .code,

          // plan
          plan_name: planName,
          issue_age_from: item.attributes.term_life_plan.data.attributes.issue_age_from,
          issue_age_to: item.attributes.term_life_plan.data.attributes.issue_age_to,
          max_cover_age: item.attributes.term_life_plan.data.attributes.max_cover_age,
          online_purchase: item.attributes.term_life_plan.data.attributes.online_purchase,
          max_sum_assured:
            item.attributes.term_life_plan.data.attributes.term_life_plan_translations.data[0].attributes
              .max_sum_assured,
          underwriting_text:
            item.attributes.term_life_plan.data.attributes.term_life_plan_translations.data[0].attributes
              .underwriting_text,

          // ***** PREMIUM *****
          premium: item.attributes.term_life_plan_premium,

          // ***** TERMS *****
          suicide_term:
            item.attributes.term_life_plan.data.attributes.term_life_plan_translations.data[0].attributes.suicide_term,
          general_term: item.attributes.term_life_plan.data.attributes.term_life_g_term_values.data,

          // ***** PROMOTION *****
          product_promotion: promotion,
          decoder_cta_link: decoderCTALink,
          decoder_cta_text: decoderCTAText,
          promotion_prices: promotionPrices,
          promotion_last_call: promotion_last_call,

          original_prices: originalPrices,
          discounted_ten_year_avg_premium: discountedTenYearAvgPremium,
          discounted_twenty_year_avg_premium: discountedTwentyYearAvgPremium,

          scenarioId: item.id,
          first_year_premium: fyp,
        };
      }
      return null;
    },
    product() {
      if (!this.simpleItems) return null;

      return {
        highlightSection: {
          scenarioCode: this.simpleItems.scenario_code,
          scenarioId: this.simpleItems.scenarioId,
          showFiveStarBadge: this.$formatShowFiveStarBadge(this.simpleItems.overall_star),
          productName: this.simpleItems.plan_name,
          insuranceCompany: {
            name: this.simpleItems.insurer_name_first + ' | ' + this.simpleItems.insurer_name_second,
            code: this.simpleItems.insurer_code,
            logoSrc: this.simpleItems.insurer_logo?.data?.attributes?.url,
            logoAlt: this.simpleItems.insurer_name_first,
          },
          userRating: {
            score: this.simpleItems.overall_star,
            numberOfRatedUser: '0',
          },
          tenlifeScore: {
            score: parseFloat(this.getScore(this.targetPeriod, this.simpleItems)),
            description: this.$t('products.termLife.tl_10LifeRating'),
            toolTipHtml: this.getTooltipContentByKey('tl_termLifeScore'),
          },
          features: [
            {
              text: this.simpleItems.underwriting_text,
              type: 'underwriting',
            },
          ],
          keyItems: [
            {
              value: this.$formatScore(this.simpleItems.coverage_score).toString(),
              key: this.$t('products.termLife.tl_termsScore'),
              topSubText: '',
              toolTipHtml: '',
              symbolValue: '',
              originalValue: '',
            },
            {
              value: this.simpleItems.promotion_prices[0]
                ? this.simpleItems.promotion_prices[0]
                : parseInt(this.simpleItems.first_year_premium).toLocaleString('en-US'),
              key: this.$t('products.termLife.tl_firstYearPremium'),
              topSubText: 'HKD',
              toolTipHtml: '',
              symbolValue: '',
              originalValue: this.simpleItems.promotion_prices[0]
                ? parseInt(this.simpleItems.first_year_premium).toLocaleString('en-US')
                : '',
            },
            {
              value: this.simpleItems.discounted_ten_year_avg_premium
                ? this.targetPeriod === '10'
                  ? this.simpleItems.discounted_ten_year_avg_premium
                  : this.simpleItems.discounted_twenty_year_avg_premium
                : this.getAvgPremium(this.targetPeriod, this.simpleItems).toString(),
              key: this.targetPeriod + this.$t('products.termLife.tl_YearAveragePremium'),
              topSubText: 'HKD',
              toolTipHtml: '',
              symbolValue: '',
              originalValue: this.simpleItems.discounted_ten_year_avg_premium
                ? this.getAvgPremium(this.targetPeriod, this.simpleItems)
                : '',
            },
            {
              value: this.getRenewal(this.$t('products.renewalUnit'), this.simpleItems.renewal_term),
              key: this.$t('products.termLife.tl_renewalTerm'),
              topSubText: '',
              toolTipHtml: '',
              symbolValue: '',
              originalValue: '',
            },
          ],
          mainButtonCTA: {
            text: this.simpleItems.decoder_cta_text,
            link: this.simpleItems.decoder_cta_link,
          },
          showShortcut: this.simpleItems.shortcut,
        },
        detailSection: {
          details: [
            {
              id: 0,
              sectionTitle: this.$t('products.termLife.tl_overview'),
              keyValues: [
                {
                  key: this.$t('products.termLife.tl_10_YearDeathBenefitScore'),
                  value: this.$formatScore(this.simpleItems.ten_year_death_score) + ' / 10',
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.termLife.tl_20_YearDeathBenefitScore'),
                  value: this.$formatScore(this.simpleItems.twenty_year_death_score) + ' / 10',
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.termLife.tl_termsScore'),
                  value: this.$formatScore(this.simpleItems.coverage_score) + ' / 10',
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.termLife.tl_renewalTerm'),
                  value: this.getRenewal(this.$t('products.renewalUnit'), this.simpleItems.renewal_term),
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.termLife.tl_sumAssured'),
                  value: 'HKD ' + this.simpleItems.sum_assured.toLocaleString('en-US'),
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.termLife.tl_10_YearAverageAnnualPremium'),
                  value: this.simpleItems.discounted_ten_year_avg_premium
                    ? 'HKD ' + this.simpleItems.discounted_ten_year_avg_premium.toLocaleString('en-US')
                    : 'HKD ' + this.$formatPrice(this.simpleItems.ten_year_avg_premium),
                  originalValue: this.simpleItems.discounted_ten_year_avg_premium
                    ? this.$formatPrice(this.simpleItems.ten_year_avg_premium)
                    : '',
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.termLife.tl_20_YearAverageAnnualPremium'),
                  value: this.simpleItems.discounted_twenty_year_avg_premium
                    ? 'HKD ' + this.simpleItems.discounted_twenty_year_avg_premium.toLocaleString('en-US')
                    : 'HKD ' + this.$formatPrice(this.simpleItems.twenty_year_avg_premium),
                  originalValue: this.simpleItems.discounted_twenty_year_avg_premium
                    ? this.$formatPrice(this.simpleItems.twenty_year_avg_premium)
                    : '',
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.termLife.tl_issueAge'),
                  value: this.$formatAgeRange(
                    this.currentLocale,
                    this.simpleItems.issue_age_from,
                    this.simpleItems.issue_age_to,
                    this.$t('products.ageUnit'),
                    this.$t('products.to')
                  ),
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.termLife.tl_lastCoverageAge'),
                  value: this.$formatAge(
                    this.currentLocale,
                    this.simpleItems.max_cover_age,
                    this.$t('products.ageUnit')
                  ),
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.termLife.tl_maximumSumAssured'),
                  value: this.simpleItems.max_sum_assured,
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.termLife.tl_onlinePurchase'),
                  value: this.simpleItems.online_purchase,
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
              ],
            },
            {
              id: 1,
              sectionTitle: this.$t('products.termLife.tl_premium'),
              keyValues:
                this.simpleItems.promotion_prices.length > 0
                  ? this.simpleItems.promotion_prices.map((item, index) => ({
                    key: this.getPremiumLabel(index + 1),
                    value:
                      'HKD ' +
                      parseFloat(item !== null ? item : this.simpleItems.original_prices[index])
                        .toFixed(0)
                        .toLocaleString('en-US'),
                    keyToolTipHtml: '',
                    expandableKeyValues: [],
                    originalValue: item !== null ? 'HKD ' +
                      parseFloat(this.simpleItems.original_prices[index])
                        .toFixed(0)
                        .toLocaleString('en-US'): '',
                  }))
                  : this.getPremium(this.simpleItems.premium, this.age, this.simpleItems.renewal_term, this.simpleItems.sum_assured).map(
                    (item, index) => ({
                      key: this.getPremiumLabel(index + 1),
                      value: 'HKD ' + parseFloat(item).toFixed(0).toLocaleString('en-US'),
                      keyToolTipHtml: '',
                      expandableKeyValues: [],
                    })
                  ),
            },
            {
              id: 2,
              sectionTitle: this.$t('products.termLife.tl_termsScore'),
              keyValues: [
                {
                  key: this.$t('products.termLife.tl_termsScore'),
                  value: this.simpleItems.coverage_score.toFixed(1).toString() + ' / 10',
                  keyToolTipHtml: '',
                  expandableKeyValues: [],
                },
                {
                  key: this.getGeneralTerm(this.simpleItems.general_term, 'T001').label,
                  value: this.$formatSymbol(this.getGeneralTerm(this.simpleItems.general_term, 'T001').value),
                  keyToolTipHtml: this.getTooltipContentByKey('tl_coverageafterAChangeofResidency'),
                  expandableKeyValues: [],
                },
                {
                  key: this.$t('products.termLife.tl_coverageunderTheFollowingDeathCauses'),
                  value: '',
                  keyToolTipHtml: '',
                  expandableKeyValues: [
                    {
                      key: this.getGeneralTerm(this.simpleItems.general_term, 'T008').label,
                      value: this.$formatSymbol(this.getGeneralTerm(this.simpleItems.general_term, 'T008').value),
                    },
                    {
                      key: this.getGeneralTerm(this.simpleItems.general_term, 'T002').label,
                      value: this.$formatSymbol(this.getGeneralTerm(this.simpleItems.general_term, 'T002').value),
                    },
                    {
                      key: this.getGeneralTerm(this.simpleItems.general_term, 'T003').label,
                      value: this.$formatSymbol(this.getGeneralTerm(this.simpleItems.general_term, 'T003').value),
                    },
                    {
                      key: this.getGeneralTerm(this.simpleItems.general_term, 'T004').label,
                      value: this.$formatSymbol(this.getGeneralTerm(this.simpleItems.general_term, 'T004').value),
                    },
                    {
                      key: this.getGeneralTerm(this.simpleItems.general_term, 'T005').label,
                      value: this.$formatSymbol(this.getGeneralTerm(this.simpleItems.general_term, 'T005').value),
                    },
                    {
                      key: this.getGeneralTerm(this.simpleItems.general_term, 'T006').label,
                      value: this.$formatSymbol(this.getGeneralTerm(this.simpleItems.general_term, 'T006').value),
                    },
                    {
                      key: this.getGeneralTerm(this.simpleItems.general_term, 'T007').label,
                      value: this.$formatSymbol(this.getGeneralTerm(this.simpleItems.general_term, 'T007').value),
                    },
                  ],
                },
              ],
            },
          ],
          promotion: {
            category:
              this.simpleItems.product_promotion?.attributes?.product_promotion_translations?.data[0].attributes
                ?.PromotionCategory,
            title:
              this.simpleItems.product_promotion?.attributes?.product_promotion_translations?.data[0].attributes
                ?.PromotionTitle,
            ctaButtonLink:
              this.simpleItems.product_promotion?.attributes?.product_promotion_translations?.data[0].attributes
                ?.CTAButtonLink,
            ctaButtonText:
              this.simpleItems.product_promotion?.attributes?.product_promotion_translations?.data[0].attributes
                ?.CTAButtonText,
            promotionId: this.simpleItems.product_promotion?.id,
            scenarioId: this.simpleItems.scenarioId,
            lastCall: this.simpleItems.promotion_last_call

          },
        },
      };
    },
  },

  methods: {
    getScore(targetPeriod, product) {
      if (targetPeriod === '10') {
        return product.ten_year_rating.toFixed(1);
      } else if (targetPeriod === '20') {
        return product.twenty_year_rating.toFixed(1);
      }
      return 0;
    },

    getGeneralTerm(terms, code) {
      let tmpTerm = null;
      tmpTerm = terms.filter((term) => {
        return code === term.attributes.gt_code;
      });
      return {
        label:
          tmpTerm[0].attributes.term_life_g_term.data.attributes.term_life_g_term_translations.data[0].attributes
            .description,
        value: tmpTerm[0].attributes.gt_value,
      };
    },

    getPremiumLabel(index) {
      if (this.currentLocale === 'en') {
        return this.$ordinalSuffix(index) + '-' + this.$t('products.termLife.tl_YearPremium');
      } else if (this.currentLocale === 'zh-Hant-HK') {
        if (index === 1) {
          return this.$t('products.termLife.tl_firstYearPremium');
        } else {
          return this.$t('products.termLife.tl_YearPremium').replace('$num', index);
        }
      }
    },

    getRenewal(label, renewalTerm) {
      if (renewalTerm === 1) {
        return this.$t('products.annualRenewal');
      }
      return label.replace('$num', renewalTerm);
    },

    getAvgPremium(targetPeriod, product) {
      if (targetPeriod === '10') {
        return this.$formatPrice(product.ten_year_avg_premium);
      } else if (targetPeriod === '20') {
        return this.$formatPrice(product.twenty_year_avg_premium);
      }
      return 0;
    },

    getAgeRange(from, to, lblAge, lblTo) {
      if (this.currentLocale === 'en') {
        return lblAge + ' ' + from + ' ' + lblTo + ' ' + to;
      } else if (this.currentLocale === 'zh-Hant-HK') {
        return from + lblTo + to + lblAge;
      }
    },

    getPremium(premium, startAge, renewalTerm, sumAssured) {
      const totalYear = 20;
      const result = new Array(20);
      const session = totalYear / renewalTerm;
      let lockedPremium = 0;

      for (let i = 0; i < session; i++) {
        // lock by the first year in the renewal_term
        lockedPremium = (premium.data.attributes['age_' + (parseInt(startAge) + i * renewalTerm)] * sumAssured / 1000).toFixed(2);
        for (let j = 0; j < renewalTerm; j++) {
          result[i * renewalTerm + j] = lockedPremium;
        }
      }
      // console.log(result);
      return result;
    },

    getTooltipContentByKey(mKey) {
      return this.termLifeTooltips?.TermLife?.ToolTips?.find(({ Key }) => Key === mKey)?.ToolTipContent ?? '';
    },
  },
};
